import { EventImpl } from '@fullcalendar/core/internal';
import React from 'react';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';

import { ExternalEvent } from './external-event';
import { GoalEvent } from './goal-event';
import { LifeAreaEvent } from './life-area-event';
import { TaskEvent } from './task-event';

export type EventViewProps = {
  event: Pick<EventImpl, 'extendedProps' | 'start' | 'end'>;
  tasks: Task[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  onComplete: (id: ID, date: Timestamp) => void;
  onClick?: (id: ID) => void;
  onInteractionComplete?: () => void;
};

const arePropsEqual = (
  prevProps: EventViewProps,
  nextProps: EventViewProps,
): boolean => {
  // Check event.start?.toISOString() specifically
  if (
    prevProps.event.start?.toISOString() !==
    nextProps.event.start?.toISOString()
  ) {
    return false;
  }

  // Check extendedProps specifically
  const prevExtProps = prevProps.event.extendedProps;
  const nextExtProps = nextProps.event.extendedProps;
  if (
    prevExtProps.taskId !== nextExtProps.taskId ||
    prevExtProps.goalId !== nextExtProps.goalId ||
    prevExtProps.isDeadline !== nextExtProps.isDeadline ||
    prevExtProps.lifeAreaId !== nextExtProps.lifeAreaId ||
    prevExtProps.externalId !== nextExtProps.externalId ||
    prevExtProps.occurrenceId !== nextExtProps.occurrenceId
  ) {
    return false;
  }

  // Check other props with shallow equality
  return !(
    prevProps.tasks !== nextProps.tasks ||
    prevProps.goals !== nextProps.goals ||
    prevProps.lifeAreas !== nextProps.lifeAreas ||
    prevProps.onComplete !== nextProps.onComplete ||
    prevProps.onClick !== nextProps.onClick ||
    prevProps.onInteractionComplete !== nextProps.onInteractionComplete
  );
};

const FullCalendarEventComponent: React.FC<EventViewProps> = ({
  event,
  tasks,
  goals,
  lifeAreas,
  onComplete,
  onClick,
  onInteractionComplete,
}) => {
  if (event.extendedProps.taskId) {
    return (
      <TaskEvent
        taskId={event.extendedProps.taskId}
        weekStartsOn={event.extendedProps.weekStartsOn}
        isDeadline={event.extendedProps.isDeadline}
        occurrenceId={event.extendedProps.occurrenceId}
        startDate={event.start?.toISOString() ?? undefined}
        endDate={event.end?.toISOString() ?? undefined}
        tasks={tasks}
        goals={goals}
        lifeAreas={lifeAreas}
        onComplete={onComplete}
        onClick={onClick}
        onInteractionComplete={onInteractionComplete}
      />
    );
  }

  if (event.extendedProps.goalId) {
    return (
      <GoalEvent
        goalId={event.extendedProps.goalId}
        startDate={event.start?.toISOString() ?? undefined}
        endDate={event.end?.toISOString() ?? undefined}
        isDeadline={event.extendedProps.isDeadline}
        goals={goals}
        lifeAreas={lifeAreas}
        onClick={onClick}
      />
    );
  }

  if (event.extendedProps.lifeAreaId) {
    return (
      <LifeAreaEvent
        lifeAreaId={event.extendedProps.lifeAreaId}
        startDate={event.start?.toISOString() ?? undefined}
        endDate={event.end?.toISOString() ?? undefined}
        lifeAreas={lifeAreas}
        onClick={onClick}
      />
    );
  }

  if (event.extendedProps.externalId) {
    return (
      <ExternalEvent
        name={event.extendedProps.name}
        url={event.extendedProps.url}
        color={event.extendedProps.color}
        startDate={event.start?.toISOString() ?? undefined}
        endDate={event.end?.toISOString() ?? undefined}
      />
    );
  }

  return null;
};

export const FullCalendarEvent = React.memo(
  FullCalendarEventComponent,
  arePropsEqual,
);
